import { useContext, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import './style.scss'
import utility from 'src/utils/utility'
import {
    Header, BackLink, CircularProgress, InstitutionBranding,
    Text, Flex, Box, tokens,
    type BrandingStepKey,
} from 'src/componentv2'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import type { BrandingInitialValues } from 'src/model/v2/interface/props'
import { PAGE_PADDING_X, fontRegular, fontBold } from 'src/model/v2/interface/constants'
import { useConfigurationChecklist } from 'src/app/dashboardv2/hooks/useConfigurationChecklist'
import { useSidebarOverride } from 'src/app/dashboardv2/layout/DashboardLayout'

const BRANDING_STEP_ORDER: BrandingStepKey[] = ['BrandingDesign', 'BrandingTheme', 'OnboardingSlide']

const InstitutionBrandingPage = () => {
    const navigate = useNavigate()
    const { institutionProfile, getInstitutionProfile, getOnboardingChecklist } = useContext(ConfigurationApiContext)
    const { loading: checklistLoading, isComplete } = useConfigurationChecklist()

    useSidebarOverride(checklistLoading ? null : { visible: isComplete })
    const [loading, setLoading] = useState(true)
    // Numeric index (0/1/2) — this is what InstitutionBranding's `initialStep` prop expects.
    const [resumeStepIndex, setResumeStepIndex] = useState(0)

    const loadResumePoint = useCallback(async () => {
        const checklist = await getOnboardingChecklist()
        const brandingTask = checklist?.find((t: any) => t.key === 'InstitutionBranding')
        const subTasks = brandingTask?.value?.subTasks ?? []

        const nextIncompleteKey = BRANDING_STEP_ORDER.find((stepKey) => {
            const sub = subTasks.find((s: any) => s.key === stepKey)
            return sub?.value?.status !== 'Completed'
        })

        // All sub-tasks already Completed -> nothing to resume into.
        // In configuration mode (isComplete) we still want to land on the
        // branding editor, just starting at step 0 instead of bouncing
        // away — the old "redirect to get-started" behaviour only makes
        // sense while still onboarding.
        if (!nextIncompleteKey) {
            if (!isComplete) {
                navigate('/dashboardv2/get-started')
            }
            return
        }

        setResumeStepIndex(BRANDING_STEP_ORDER.indexOf(nextIncompleteKey))
    }, [getOnboardingChecklist, navigate, isComplete])

    useEffect(() => {
        if (checklistLoading) return
        (async () => {
            if (!institutionProfile) {
                await getInstitutionProfile()
            }
            await loadResumePoint()
            setLoading(false)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checklistLoading])

    const handleStepComplete = (completedStep: BrandingStepKey) => {
        const isLastStep = completedStep === BRANDING_STEP_ORDER[BRANDING_STEP_ORDER.length - 1]
        // Only meaningful mid-onboarding — once setup is already complete
        // there's no wizard to graduate out of.
        if (isLastStep && !isComplete) {
            navigate('/dashboardv2/get-started')
        }
    }

    if (loading || checklistLoading) return null

    const initialValues: BrandingInitialValues = {
        variant: institutionProfile?.themeVariant,
        primaryColor: institutionProfile?.primaryBrandColour,
        secondaryColor: institutionProfile?.secondaryBrandColour,
        darkSecondaryColor: institutionProfile?.tertiaryBrandColour,
        cornerRadius: institutionProfile?.cornerRadius,
        typography: institutionProfile?.typography,
        cardStyle: institutionProfile?.cardStyle,
    }

    const content = (
        <Box topMargin="2">
            <InstitutionBranding
                initialStep={resumeStepIndex}
                initial={initialValues}
                onStepComplete={handleStepComplete}
            />
        </Box>
    )

    // ---- Setup complete: configuration shell (sidebar + tabbed header) ----
    if (isComplete) {
        return (
            <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
                <Flex direction="column" gap="0.6" items="flex-start">
                    <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                        Institution Branding Setup
                    </Text>
                    <Text fontSize="1.4" color={tokens.textMuted} style={fontRegular}>
                        Define how the mobile and internet banking applications will appear to customers.
                    </Text>
                </Flex>

                {content}
            </div>
        )
    }

    // ---- Still onboarding: original wizard shell ----
    return (
        <Box bgColor={tokens.pageBg} minHeight="100vh">
            <Header sticky initials="QO" />

            <div className="institution-branding-page" style={{ padding: `3.2rem ${PAGE_PADDING_X}` }}>
                <BackLink onClick={() => navigate(-1)}>
                    Go back
                </BackLink>

                <Flex justify="space-between" items="flex-start" topMargin="1.6">
                    <Box className="institution-header">
                        <Text fontSize="2.4" style={fontBold}>
                            Institution Branding Setup
                        </Text>

                        <Text
                            topMargin="0.6"
                            fontSize="1.4"
                            color={tokens.textMuted}
                            style={fontRegular}
                        >
                            Define how the mobile and internet banking applications will appear to customers.
                        </Text>
                    </Box>

                    <CircularProgress value={50} caption="Step 2 of 4" />
                </Flex>

                {content}
            </div>
        </Box>
    )
}

export default utility.routeData('institution-branding', 'Institution Branding Setup', <InstitutionBrandingPage />)